<template>
  <component
    :is="resolveLayoutVariant"
    :class="`skin-variant--${skinVariant}`"
  >
    <connection-banner />
    <transition
      :name="routeTransition"
      mode="out-in"
      appear
    >
      <router-view
        :key="$route.fullPath"
      />
    </transition>

    <v-snackbar
      bottom
      right
      :value="updateExists"
      :timeout="-1"
    >
      バージョンアップデートがあります。
      <v-btn
        text
        :ripple="false"
        @click="refreshApp"
      >
        更新する
      </v-btn>
    </v-snackbar>
  </component>
</template>

<script>
import {
  computed,
  ref,
  onMounted,
  getCurrentInstance,
} from '@vue/composition-api'
import { useRouter } from '@core/utils'
import themeConfig from '@themeConfig'
// Dynamic vh
import useDynamicVh from '@core/utils/useDynamicVh'
// Layouts
import LayoutBlank from '@/layouts/variants/blank/LayoutBlank.vue'
import LayoutAdmin from '@/layouts/variants/LayoutAdmin.vue'
import LayoutWaiter from '@/layouts/variants/LayoutWaiter.vue'
import LayoutCustom from '@/layouts/variants/LayoutCustom.vue'
import LayoutCast from '@/layouts/variants/LayoutCast.vue'
import ConnectionBanner from '@/components/ConnectionBanner.vue'

import 'vue2-animate/dist/vue2-animate.min.css'

import MyAbilityApi from '@/api/v2/security/MyAbility'
import { initialAbility } from '@/plugins/acl/config'

export default {
  components: {
    LayoutBlank,
    LayoutAdmin,
    LayoutWaiter,
    LayoutCustom,
    LayoutCast,
    ConnectionBanner,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const { route } = useRouter()

    const { skinVariant, routeTransition } = themeConfig.app
    const refreshing = ref(false)
    const worker = ref(null)
    const updateExists = ref(false)

    const resolveLayoutVariant = computed(() => {
      if (route.value.meta.layout === 'blank') return 'layout-blank'

      const layoutNames = {
        admin: 'layout-admin',
        waiter: 'layout-waiter',
        cast: 'layout-cast',
        custom: 'layout-custom',
      }
      const layoutName = layoutNames[vm.currentUser?.role || route.value.meta.layout]

      if (layoutName) return layoutName

      return null
    })

    useDynamicVh()

    const showRefreshUI = e => {
      if (e.detail) {
        worker.value = e.detail
        updateExists.value = true
      }
    }

    document.addEventListener('swUpdated', showRefreshUI, { once: true })
    // Refresh all open app tabs when a new service worker is installed.
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => {
          if (refreshing.value) return
          refreshing.value = true
          window.location.reload()
        },
      )
    }

    const refreshApp = () => {
      updateExists.value = false

      if (!worker.value) {
        // if no worker return without post messaging
        return
      }
      worker.value.waiting.postMessage({ type: 'SKIP_WAITING' })
    }

    const getDeviceType = () => {
      const ua = navigator.userAgent
      if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        return 'tablet'
      }
      if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
        return 'mobile'
      }

      return 'desktop'
    }

    onMounted(async () => {
      if (vm.currentUser) {
        const res = await MyAbilityApi.getMyAbilities()
        if (res?.data?.abilities) {
          const { abilities } = res.data
          const myLatestAbilities = [...abilities, ...initialAbility]
          vm.$ability.update(myLatestAbilities)
          localStorage.setItem('userAbilities', JSON.stringify(myLatestAbilities))
        }
      }
    })

    return {
      updateExists,
      refreshApp,
      resolveLayoutVariant,
      skinVariant,
      routeTransition,
    }
  },
}
</script>
